import { useCallback, useEffect, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { InternalApiKeysService } from "../services/internalApiKeys.service";
import type { InternalApiKey } from "../types/internalApiKeys.types";

export interface UseInternalApiKeysResult {
  keys: InternalApiKey[];
  isLoading: boolean;
  error: string | null;
  /** False after a 401/403, or the 404 the backend answers staff off the allowlist. */
  hasAccess: boolean;
  reload: () => void;
  /** Swaps one key in place after a mutation answered with it, without a full reload. */
  replace: (key: InternalApiKey) => void;
}

interface ListRead {
  nonce: number;
  keys: InternalApiKey[];
  error: string | null;
}

/** Loading is derived from whether the last answer belongs to the current read, never set inside the effect. */
export function useInternalApiKeys(): UseInternalApiKeysResult {
  const [nonce, setNonce] = useState(0);
  const [read, setRead] = useState<ListRead | null>(null);
  const [hasAccess, setHasAccess] = useState(true);

  useEffect(() => {
    let cancelled = false;
    InternalApiKeysService.list()
      .then((keys) => {
        if (cancelled) return;
        setHasAccess(true);
        setRead({ nonce, keys, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = HttpApiService.isAxiosError(err) ? err.response?.status : undefined;
        if (HttpApiService.isAccessDeniedError(err) || status === 404) setHasAccess(false);
        setRead((previous) => ({
          nonce,
          keys: previous?.keys ?? [],
          error: HttpApiService.getErrorMessage(err),
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const replace = useCallback((key: InternalApiKey) => {
    setRead((previous) =>
      previous
        ? { ...previous, keys: previous.keys.map((row) => (row.id === key.id ? key : row)) }
        : previous,
    );
  }, []);

  return {
    keys: read?.keys ?? [],
    isLoading: read?.nonce !== nonce,
    error: read?.nonce === nonce ? read.error : null,
    hasAccess,
    reload,
    replace,
  };
}
